import React, { useState } from 'react';
import { UserPlus, Check, AlertTriangle } from 'lucide-react';
import { invokeEdge } from '@/lib/edgeInvoke';
import { useI18n } from '@/i18n/LanguageContext';
import { TRADES, tradeLabel } from '@/data/constants';

/**
 * Admin-only form that calls the `admin-create-subcontractor` edge function.
 * The function self-verifies the caller's mkt_profiles.is_admin, so no admin
 * token is handled manually — invokeEdge attaches a freshly refreshed session
 * token and surfaces the function's real error body (an expired session used to
 * appear as the opaque "Edge Function returned a non-2xx status code").
 */
const AdminCreateSubcontractor: React.FC = () => {
  const { t, lang } = useI18n();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('CA');
  const [trades, setTrades] = useState<string[]>([]);

  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState('');
  const [err, setErr] = useState('');
  /** Set when the email belongs to a profile of another user type. */
  const [convertOffer, setConvertOffer] = useState<{ userType: string } | null>(null);

  const toggleTrade = (slug: string) =>
    setTrades((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));

  const submit = async (e: React.FormEvent | React.MouseEvent, convertExisting = false) => {
    e.preventDefault();

    setOk('');
    setErr('');
    if (!convertExisting) setConvertOffer(null);

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setErr(t('auth.errEmail'));
      return;
    }
    if (password.length < 10) {
      setErr(t('admin.createSubErrPassword'));
      return;
    }

    setBusy(true);
    const r = await invokeEdge('admin-create-subcontractor', {
      email: email.trim().toLowerCase(),
      password,
      companyName: companyName.trim() || undefined,
      contactName: contactName.trim() || undefined,
      phone: phone.trim() || undefined,
      city: city.trim() || undefined,
      state: state.trim() || undefined,
      trades,
      language: lang,
      convertExisting,
    });
    setBusy(false);

    if (r.ok && r.data?.ok) {
      setConvertOffer(null);
      setOk(t('admin.createSubOk').replace('{email}', r.data.email || email.trim().toLowerCase()));
      setEmail('');
      setPassword('');
      setCompanyName('');
      setContactName('');
      setPhone('');
      setCity('');
      setState('CA');
      setTrades([]);
      return;
    }

    // An existing profile of another type can be converted instead of failing.
    if (r.error === 'already_exists' && r.data?.convertible) {
      setConvertOffer({ userType: String(r.data.userType || 'homeowner') });
      setErr('');
      return;
    }
    if (r.error === 'already_exists') {
      setErr(t('admin.createSubErrExists'));
      return;
    }
    if (r.error === 'bad_request' && r.data?.field === 'email') {
      setErr(t('auth.errEmail'));
      return;
    }
    if (r.error === 'bad_request') {
      setErr(t('admin.createSubErrPassword'));
      return;
    }
    // Real cause (expired session, missing admin flag, server detail) instead
    // of a generic sentence the operator cannot act on.
    setErr(r.message || t('admin.createSubErrGeneric'));
  };


  const input =
    'w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-900';
  const label = 'block text-xs font-bold uppercase tracking-wide text-slate-500';

  return (
    <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex flex-wrap items-center gap-2">
        <UserPlus className="h-5 w-5 text-orange-500" />
        <h2 className="text-xl font-extrabold text-slate-900">{t('admin.createSubTitle')}</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">{t('admin.createSubSub')}</p>

      {ok && (
        <p className="mt-4 flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
          <Check className="mt-0.5 h-4 w-4 shrink-0" /> {ok}
        </p>
      )}
      {err && (
        <p className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {err}
        </p>
      )}
      {convertOffer && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
          <p className="flex items-start gap-2 text-sm font-semibold text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {lang === 'es'
              ? `Ese correo ya pertenece a una cuenta de tipo "${convertOffer.userType}". Puede convertirla en cuenta de contratista sin cambiar su contraseña.`
              : `That email already belongs to a "${convertOffer.userType}" account. You can convert it to a contractor account without changing their password.`}
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={(e) => submit(e, true)}
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            <UserPlus className="h-4 w-4" />
            {lang === 'es' ? 'Convertir a contratista' : 'Convert to contractor'}
          </button>
        </div>
      )}

      <form onSubmit={(e) => submit(e, false)} className="mt-6 space-y-5">

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={label} htmlFor="cs-email">
              {t('common.email')}
            </label>
            <input
              id="cs-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`mt-1.5 ${input}`}
            />
          </div>
          <div>
            <label className={label} htmlFor="cs-password">
              {t('auth.password')}
            </label>
            <input
              id="cs-password"
              type="text"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`mt-1.5 ${input}`}
            />
            <p className="mt-1 text-xs text-slate-400">{t('admin.createSubPasswordHint')}</p>
          </div>
          <div>
            <label className={label} htmlFor="cs-company">
              {t('auth.companyName')}
            </label>
            <input
              id="cs-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className={`mt-1.5 ${input}`}
            />
          </div>
          <div>
            <label className={label} htmlFor="cs-contact">
              {t('auth.contactName')}
            </label>
            <input
              id="cs-contact"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              className={`mt-1.5 ${input}`}
            />
          </div>
          <div>
            <label className={label} htmlFor="cs-phone">
              {t('common.phone')}
            </label>
            <input
              id="cs-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`mt-1.5 ${input}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label} htmlFor="cs-city">
                {t('common.city')}
              </label>
              <input
                id="cs-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={`mt-1.5 ${input}`}
              />
            </div>
            <div>
              <label className={label} htmlFor="cs-state">
                {t('common.state')}
              </label>
              <input
                id="cs-state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className={`mt-1.5 ${input}`}
              />
            </div>
          </div>
        </div>

        <div>
          <p className={label}>{t('auth.tradeCategories')}</p>
          <p className="mt-1 text-xs text-slate-400">{t('auth.selectTrades')}</p>
          <div className="mt-3 flex max-h-56 flex-wrap gap-2 overflow-y-auto rounded-lg border border-slate-200 p-3">
            {TRADES.map((tr) => {
              const on = trades.includes(tr.slug);
              return (
                <button
                  key={tr.slug}
                  type="button"
                  onClick={() => toggleTrade(tr.slug)}
                  aria-pressed={on}
                  className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                    on ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {tradeLabel(tr.slug, lang)}
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          <UserPlus className="h-4 w-4" />
          {busy ? t('admin.createSubCreating') : t('admin.createSubBtn')}
        </button>
      </form>
    </div>
  );
};

export default AdminCreateSubcontractor;
